// ModalFooter — 모달 하단 액션 바의 표준.
//
// 지금까지 38곳이 손으로 짜던 "취소(외곽선) + 저장(brand-500) + disabled 가드" 푸터를
// 한 컴포넌트로 흡수한다. ModalShell 의 footer 슬롯에 꽂아 쓴다.
//
//   <ModalShell ... footer={
//     <ModalFooter onCancel={close} confirmLabel="저장" onConfirm={save} confirmDisabled={!canSave} />
//   } />
//
// 핵심: onConfirm 이 Promise 를 반환하면 셸이 자동으로 busy 처리(버튼 비활성 + 라벨 교체)한다.
//   → 각 모달이 isPending/busy state 를 직접 들고 다닐 필요가 없다.

import { useState, type ReactNode } from "react";

type Tone = "brand" | "outline" | "ghost" | "danger" | "emerald";

const TONE_CLASS: Record<Tone, string> = {
  brand:
    "bg-brand-500 text-white hover:bg-brand-600 disabled:opacity-40 disabled:cursor-not-allowed",
  outline:
    "border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:bg-white/[0.03] dark:text-gray-300 dark:hover:bg-white/[0.06] disabled:opacity-40",
  ghost:
    "text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-white/[0.06] disabled:opacity-40",
  danger:
    "bg-rose-500 text-white hover:bg-rose-600 disabled:opacity-40 disabled:cursor-not-allowed",
  emerald:
    "bg-emerald-500 text-white hover:bg-emerald-600 disabled:opacity-40 disabled:cursor-not-allowed",
};

export interface ModalFooterButton {
  label: string;
  onClick?: () => void | Promise<void>;
  tone?: Tone;
  disabled?: boolean;
  /** busy 동안 보여줄 라벨(예: "저장 중…"). 없으면 라벨 유지 + 비활성. */
  loadingLabel?: string;
  autoFocus?: boolean;
  type?: "button" | "submit";
}

export interface ModalFooterProps {
  /** 보조(취소) 버튼 핸들러. 보통 onClose. 없으면 취소 버튼을 숨긴다. */
  onCancel?: () => void;
  cancelLabel?: string;
  /** 주 액션. confirmLabel 이 있을 때만 렌더. */
  confirmLabel?: string;
  onConfirm?: () => void | Promise<void>;
  confirmTone?: Tone;
  confirmDisabled?: boolean;
  confirmLoadingLabel?: string;
  /** 외부 busy 강제(직접 들고 있는 상태가 있으면). 미지정 시 onConfirm Promise 로 자동 추적. */
  busy?: boolean;
  /** 주/보조 외 추가 버튼(수정·출력·복사·확정 등). confirm 왼쪽에 순서대로. */
  extraActions?: ModalFooterButton[];
  /** 좌측 영역(카운트·안내문·메타). align="between" 과 함께 양끝 정렬. */
  startSlot?: ReactNode;
  align?: "end" | "between";
}

function FooterButton({
  btn,
  busy,
  fallbackTone,
}: {
  btn: ModalFooterButton;
  busy: boolean;
  fallbackTone: Tone;
}) {
  const [localBusy, setLocalBusy] = useState(false);
  const isBusy = busy || localBusy;
  const tone = btn.tone ?? fallbackTone;

  const handle = async () => {
    if (!btn.onClick) return;
    const r = btn.onClick();
    if (r instanceof Promise) {
      setLocalBusy(true);
      try {
        await r;
      } finally {
        setLocalBusy(false);
      }
    }
  };

  return (
    <button
      type={btn.type ?? "button"}
      autoFocus={btn.autoFocus}
      disabled={btn.disabled || isBusy}
      onClick={handle}
      className={`inline-flex h-9 items-center justify-center rounded-lg px-4 text-sm font-medium shadow-theme-xs transition ${TONE_CLASS[tone]}`}
    >
      {isBusy && btn.loadingLabel ? btn.loadingLabel : btn.label}
    </button>
  );
}

export function ModalFooter({
  onCancel,
  cancelLabel = "취소",
  confirmLabel,
  onConfirm,
  confirmTone = "brand",
  confirmDisabled,
  confirmLoadingLabel,
  busy = false,
  extraActions = [],
  startSlot,
  align = "end",
}: ModalFooterProps) {
  const justify = align === "between" ? "justify-between" : "justify-end";

  return (
    <div
      className={`flex items-center gap-2 border-t border-gray-100 px-5 py-3 dark:border-gray-800 ${justify}`}
    >
      {startSlot != null && <div className="min-w-0 text-xs text-gray-500">{startSlot}</div>}
      <div className="flex items-center gap-2">
        {onCancel && (
          <FooterButton
            btn={{ label: cancelLabel, onClick: onCancel, tone: "outline" }}
            busy={false}
            fallbackTone="outline"
          />
        )}
        {extraActions.map((a, i) => (
          <FooterButton key={i} btn={a} busy={busy} fallbackTone="outline" />
        ))}
        {confirmLabel && (
          <FooterButton
            btn={{
              label: confirmLabel,
              onClick: onConfirm,
              tone: confirmTone,
              disabled: confirmDisabled,
              loadingLabel: confirmLoadingLabel,
            }}
            busy={busy}
            fallbackTone="brand"
          />
        )}
      </div>
    </div>
  );
}

export default ModalFooter;
